// Decodes a NetSuite custitem_mapping string back into a configuration, the
// inverse of the fixed-width "MAPPING" operation encoder (operations.json).
//
// The encoder concatenates fixed-width tokens in a fixed order:
//   [DROPDOWN_SLOTS] + [INPUT_SLOTS] + [DROPDOWN_INPUT_SLOTS] + [QTABLE_SLOTS]
// We extract the encoder's own slot spec at runtime (so it never drifts) and
// walk the string slot-by-slot, inverting each token back to an answer.
//
// Validated by round-trip: decode -> apply -> re-encode reproduces the exact
// original string across Box-Type / O-Type / Hinge configs. Slots whose token
// doesn't resolve to a real answer (e.g. the derived DIMENSIONS_SIZE that shares
// CELL_OD's cell) simply produce nothing, so conflicts self-resolve.

import type { Operation, Question } from "../types";
import { stripHtml } from "./text";

type DropdownSlot = { slot_id: string; cpq_question: string; width: number; null_token: string; map: Record<string, string> };
type InputSlot = { slot_id: string; cpq_question: string; cpq_answer: string; width: number; null_token: string; kind?: string; max_den?: number };
type QTableSlot = {
  slot_id: string; table_code: string; column: string; width: number; null_token: string;
  map_id?: string; encode_as?: string; read_as?: string; cpq_question?: string; cpq_answer?: string; kind?: string; max_den?: number;
};
export type MappingSpec = {
  DROPDOWN_SLOTS: DropdownSlot[];
  INPUT_SLOTS: InputSlot[];
  DROPDOWN_INPUT_SLOTS: InputSlot[];
  QTABLE_SLOTS: QTableSlot[];
  TOKEN_MAPS: Record<string, Record<string, string>>;
};

/** The token map for a DROPDOWN question, or null when the question has no dropdown slot at
 *  all (so nothing is missing from it).
 *
 *  A dropdown slot carries its map INLINE, on the slot. The top-level TOKEN_MAPS is a set of
 *  named, shared maps that only QTABLE_SLOTS reference by map_id -- reading a dropdown's
 *  tokens from there returns nothing for every question, which reads exactly like "no option
 *  has a token" and would have had the combo box re-creating all 28 hood sizes on first use.
 *  decodeMapping uses `s.map` for dropdowns, so this matches it. */
export function dropdownTokenMap(
  spec: MappingSpec | null | undefined,
  questionCode: string
): Record<string, string> | null {
  const slot = (spec?.DROPDOWN_SLOTS ?? []).find((s) => s.cpq_question === questionCode);
  return slot ? slot.map || {} : null;
}


export type DecodedConfig = {
  // dropdown selections to apply (one answer per question)
  selects: Array<{ question: string; answerCode: string }>;
  // raw cell/text values to apply, keyed by "<question-or-table>/<answer-or-column>"
  values: Record<string, string>;
};

// --- helpers copied verbatim from the encoder (pure) so matching is identical ---
function normalizeKey(val: unknown): string {
  if (val == null) return "";
  let s = String(val);
  if (s.indexOf("/") !== -1) s = s.split("/").pop() as string;
  return s.replace(/"/g, "").replace(/\s+/g, "").replace(/\./g, "_");
}
function gcd(a: number, b: number): number {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = b; b = a % b; a = t; }
  return a;
}
export function normalizeMixedInches(raw: unknown, maxDen = 16): string | null {
  if (raw == null || raw === false) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/"/g, "").replace(/\s+/g, " ").trim();
  if (/^(n\/a|na)$/i.test(s)) return "N/A";
  let m = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    let n = parseInt(m[1], 10), d = parseInt(m[2], 10);
    if (d === 0) return null;
    const g = gcd(n, d); n /= g; d /= g;
    if (n >= d) { const w = Math.floor(n / d), r = n % d; return r === 0 ? String(w) : `${w}-${r}/${d}`; }
    return `${n}/${d}`;
  }
  m = s.match(/^(\d+)\s*[- ]\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    let w = parseInt(m[1], 10), n = parseInt(m[2], 10), d = parseInt(m[3], 10);
    if (d === 0) return String(w);
    const g = gcd(n, d); n /= g; d /= g;
    if (n >= d) { w += Math.floor(n / d); n %= d; }
    return n === 0 ? String(w) : `${w}-${n}/${d}`;
  }
  m = s.match(/^(\d+)(?:\.(\d+))?$/);
  if (m) {
    let w = parseInt(m[1], 10);
    const frac = m[2] || "";
    if (!frac) return String(w);
    const fv = parseFloat("0." + frac);
    if (isNaN(fv)) return String(w);
    let n = Math.round(fv * maxDen); const d = maxDen;
    if (n >= d) { w += 1; n = 0; }
    if (n === 0) return String(w);
    const g = gcd(n, d); return `${w}-${n / g}/${d / g}`;
  }
  return s;
}

// Extract the encoder's slot spec by running it with stub APIs and capturing the
// slot arrays via a global. Cached — the spec is static for a given operations.json.
let specCache: MappingSpec | null = null;
export function extractMappingSpec(operations: Operation[]): MappingSpec | null {
  if (specCache) return specCache;
  const op = operations.find((o) => o.custrecord_cpqc_operation_code === "MAPPING");
  if (!op) return null;
  const code = op.custrecord_cpqc_code_exec.replace(
    /var (DROPDOWN_SLOTS|INPUT_SLOTS|DROPDOWN_INPUT_SLOTS|QTABLE_SLOTS|TOKEN_MAPS)\s*=/g,
    "var $1 = (globalThis).__CPQ_SPEC.$1 ="
  );
  const g = globalThis as unknown as { __CPQ_SPEC?: Record<string, unknown> };
  g.__CPQ_SPEC = {};
  try {
    new Function(
      "getAnswerCode", "getValue", "getAnswerLabel", "setValue", "getTable",
      code
    )(() => "ANS CODE NF", () => null, () => "", () => {}, () => undefined);
  } catch {
    // the encode loops may reference runtime values; the spec is captured before that
  }
  const cap = g.__CPQ_SPEC as Partial<MappingSpec>;
  if (!cap.DROPDOWN_SLOTS || !cap.QTABLE_SLOTS) return null;
  specCache = {
    DROPDOWN_SLOTS: cap.DROPDOWN_SLOTS ?? [],
    INPUT_SLOTS: cap.INPUT_SLOTS ?? [],
    DROPDOWN_INPUT_SLOTS: cap.DROPDOWN_INPUT_SLOTS ?? [],
    QTABLE_SLOTS: cap.QTABLE_SLOTS ?? [],
    TOKEN_MAPS: cap.TOKEN_MAPS ?? {},
  };
  return specCache;
}

// Invert a token map. The encoder stores each token left-padded with "0" to the
// slot width (enforceWidth), so a map value like "2" (bolt qty) is written "02".
// We must invert on the PADDED token, or every slot whose value is shorter than
// its width fails to decode and the field is silently dropped.
const invert = (map: Record<string, string>, width = 0): Record<string, string> => {
  const inv: Record<string, string> = {};
  for (const k of Object.keys(map)) {
    const tok = width ? padLeft(map[k], width) : map[k];
    if (!(tok in inv)) inv[tok] = k;
  }
  return inv;
};
const stripPad = (tok: string): string => tok.replace(/^0+/, "");

export function decodeMapping(
  mapping: string,
  spec: MappingSpec,
  questions: Question[]
): DecodedConfig {
  const byCode = new Map(questions.map((q) => [q.custrecord_cpqc_question_code, q]));
  // A mapping written before a slot was APPENDED is read with that slot peeled off, exactly
  // as the NetSuite decoder does it (see LEGACY_APPENDED_KEYS there). Without this, an older
  // string is not merely missing its last value -- the dropdown section reads two characters
  // too many and every later section is shifted, so Item Template and Edit Config would load
  // a plausible-looking configuration that is wrong throughout.
  //
  // Keyed on TOTAL length, the same proxy the NetSuite side uses: these strings are positional
  // and delimiter-free, so length is the only schema stamp they carry.
  const APPENDED_SLOT_KEYS = ["FLAT_SIDE_ORIENTATION_2"];
  // Slots WIDENED in place, newest first, with the width they had before: a mapping written
  // earlier is one character short per entry and is read with the slot at its old width
  // (FLANGE_TO_NOZZLE_END 2 -> 3 on 2026-10-19, so list ids past 99 can be encoded; the
  // NetSuite decoder's LEGACY_WIDENED_SLOTS is the same list).
  const WIDENED_SLOTS: Array<{ slot_id: string; width: number }> = [{ slot_id: "FLANGE_TO_NOZZLE_END", width: 2 }];
  const totalOf = (dropdowns: MappingSpec["DROPDOWN_SLOTS"]) =>
    [dropdowns, spec.INPUT_SLOTS, spec.DROPDOWN_INPUT_SLOTS, spec.QTABLE_SLOTS]
      .flat()
      .reduce((n, sl) => n + Number((sl as { width?: number }).width ?? 0), 0);
  // A layout for every combination: p newest appended slots peeled, w newest widened slots
  // narrowed. Two greedy passes are not enough -- peeling the appended slot first takes a
  // 369-character string (written after Dual Flat Side, before the widening) to 368 and the
  // narrowing then lands on 367. The first candidate whose width matches wins; none matching
  // leaves the current layout, as before.
  const layout = (p: number, w: number) => {
    const peeled = new Set(APPENDED_SLOT_KEYS.slice(APPENDED_SLOT_KEYS.length - p));
    const narrow = new Map(WIDENED_SLOTS.slice(WIDENED_SLOTS.length - w).map((x) => [x.slot_id, x.width]));
    return spec.DROPDOWN_SLOTS.filter((sl) => !peeled.has(sl.slot_id)).map((sl) => (narrow.has(sl.slot_id) ? { ...sl, width: narrow.get(sl.slot_id)! } : sl));
  };
  let dropdownSlots = spec.DROPDOWN_SLOTS;
  search: for (let p = 0; p <= APPENDED_SLOT_KEYS.length; p++) {
    for (let w = 0; w <= WIDENED_SLOTS.length; w++) {
      const cand = layout(p, w);
      if (totalOf(cand) === mapping.length) { dropdownSlots = cand; break search; }
    }
  }
  const selects: DecodedConfig["selects"] = [];
  const values: DecodedConfig["values"] = {};
  let pos = 0;
  const take = (w: number) => { const s = mapping.slice(pos, pos + w); pos += w; return s; };

  // Resolve a normalized answer-code key to a real answer code on a question.
  const answerByKey = (question: string, key: string): string | undefined =>
    byCode.get(question)?._answers.find(
      (a) => normalizeKey(a.custrecord_cpqc_answer_code) === key
    )?.custrecord_cpqc_answer_code;
  // Resolve a normalized label to a real answer code (dropdown-to-input slots).
  const answerByLabel = (question: string, label: string): string | undefined =>
    byCode.get(question)?._answers.find(
      (a) => normalizeMixedInches(stripHtml(a.custrecord_cpqc_answer_label_html) || a.name) === label
    )?.custrecord_cpqc_answer_code;
  const addValue = (k: string, v: string) => { if (!(k in values)) values[k] = v; }; // first wins

  for (const s of dropdownSlots) {
    const tok = take(s.width);
    if (tok === padLeft(s.null_token, s.width)) continue;
    const key = invert(s.map || {}, s.width)[tok];
    if (key == null) continue;
    const code = answerByKey(s.cpq_question, key);
    if (code) selects.push({ question: s.cpq_question, answerCode: code });
  }
  for (const s of spec.INPUT_SLOTS) {
    const tok = take(s.width);
    if (tok === s.null_token) continue;
    const v = stripPad(tok);
    if (v) addValue(`${s.cpq_question}/${s.cpq_answer}`, v);
  }
  for (const s of spec.DROPDOWN_INPUT_SLOTS) {
    const tok = take(s.width);
    if (tok === s.null_token) continue;
    const v = stripPad(tok);
    if (!v) continue;
    const code = answerByLabel(s.cpq_question, v);
    if (code) selects.push({ question: s.cpq_question, answerCode: code });
  }
  for (const s of spec.QTABLE_SLOTS) {
    const tok = take(s.width);
    if (tok === padLeft(s.null_token, s.width)) continue;
    if (s.read_as === "dropdown" && s.cpq_question) {
      const key = invert(spec.TOKEN_MAPS[s.map_id || ""] || {}, s.width)[tok];
      if (key == null) continue;
      const code = answerByKey(s.cpq_question, key);
      if (code) selects.push({ question: s.cpq_question, answerCode: code });
    } else if (s.encode_as === "input") {
      const v = stripPad(tok);
      if (v) addValue(`${s.table_code}/${s.column}`, v);
    } else {
      const v = invert(spec.TOKEN_MAPS[s.map_id || ""] || {}, s.width)[tok];
      if (v == null) continue;
      // read_as:"input" slots store against the question/answer; others the cell.
      if (s.read_as === "input" && s.cpq_question) addValue(`${s.cpq_question}/${s.cpq_answer || s.cpq_question}`, v);
      else addValue(`${s.table_code}/${s.column}`, v);
    }
  }
  return { selects, values };
}

function padLeft(s: string, w: number, ch = "0"): string {
  s = String(s);
  while (s.length < w) s = ch + s;
  return s;
}
